import { GridBaseDataProvider } from '../../../../../../grid/GridBaseDataProvider';
import { TargetsColumn, TargetsColumnKeys, TargetsReadRecordsParams } from '../types';
import {
  GetRowsRequest,
  GetSummaryRequest,
  SetFiltersRequest,
  UpdateSettingsRequest,
  UpdateSettingsResponse,
} from '../../../../../../grid/types/common';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { ConsoleRequest, DataProviderType } from '../../../../../../utils/request';
import { ExtJsFilter } from '../../../../../../grid/utils/filters/types';
import { convertGridFiltersToExtJsFilters } from '../../../../../../grid/utils/filters';

export class TargetsGridDataProvider extends GridBaseDataProvider<TargetsColumn, TargetsColumnKeys> {
  private getCountPromise: Promise<number> | undefined;
  private countPromiseFunctions: {
    resolve: Function;
    reject: Function;
  };

  private columnKeyToSortableField: Map<TargetsColumnKeys, string> = new Map([
    [TargetsColumnKeys.POLICY, 'assignedPolicy.policyName'],
  ]);

  private gridFilters: Array<ExtJsFilter<TargetsColumnKeys>>;

  //@ts-ignore
  async getSummary(request: GetSummaryRequest<TargetsColumnKeys>) {
    this.setupCountPromise();
    const numItems = await this.getCountPromise;
    delete this.getCountPromise;

    return {
      summary: '',
      numItems: numItems,
    };
  }

  async getRows(request: GetRowsRequest<TargetsColumn, TargetsColumnKeys>): Promise<any> {
    const response = await this.readRecords(request);

    return {
      success: response.success,
      rawEvents: response.rawEvents,
    };
  }

  async setFilters(request: SetFiltersRequest<TargetsColumnKeys>): Promise<{} | undefined> {
    const policyFilter = request.filters.find(filter => filter[TargetsColumnKeys.POLICY]);
    if (!policyFilter) {
      console.error('Could not find policy filter.');
      return;
    }

    const policyFilterValue = policyFilter[TargetsColumnKeys.POLICY];
    const consoleFilters = request.filters.filter(filter => !filter[TargetsColumnKeys.POLICY]);

    if (policyFilterValue && policyFilterValue[Object.keys(policyFilterValue)[0]]) {
      consoleFilters.push({
        [TargetsColumnKeys.POLICY_ID]: policyFilterValue,
      });
    }

    this.gridFilters = convertGridFiltersToExtJsFilters(consoleFilters);
  }

  private async readRecords(request: Partial<GetRowsRequest<TargetsColumn, TargetsColumnKeys>>): Promise<any> {
    if (request.pageSize === undefined || request.pageNumber === undefined) {
      throw Error('No page size sent for grid request!');
    }

    const gridTargets = this.dependencies.targets as Array<ProtectedEntity>;
    const targetIds = gridTargets.map(target => target._id);

    const payload: TargetsReadRecordsParams = {
      limit: request.pageSize,
      page: request.pageNumber,
      protectedEntityId: targetIds,
    };

    if (request.sortColumnName && request.sortDirection) {
      payload.sort = this.columnKeyToSortableField.has(<TargetsColumnKeys>request.sortColumnName)
        ? this.columnKeyToSortableField.get(<TargetsColumnKeys>request.sortColumnName)
        : request.sortColumnName;
      payload.dir = request.sortDirection;
    }

    if (this.gridFilters && this.gridFilters.length !== 0) {
      payload.filter = this.gridFilters;
    }

    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.EPS,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'getProtectedEntityById',
      payload: payload,
    });

    this.countPromiseFunctions.resolve(response.total);

    return {
      success: response.success,
      rawEvents: response.data,
    };
  }

  private setupCountPromise(): void {
    if (!this.getCountPromise) {
      this.getCountPromise = new Promise((resolve, reject) => {
        this.countPromiseFunctions = { resolve, reject };
      });
    }
  }

  // we do nothing when settings are updated
  async updateSettings(params: UpdateSettingsRequest<TargetsColumn>): Promise<UpdateSettingsResponse> {
    return {};
  }
}
